import {
  useCallback, useContext, useRef, useState, type ReactNode,
} from 'react';
import { Languages } from 'lucide-react';
import { Button, cn } from './ui';
import { useSession } from '@/core/session';
import type { DocLang } from '@/core/reports/docStrings';
import { useT } from '@/core/i18n';
import { ExportLanguageContext } from './exportLanguageContext';

/* =============================================================================
 * Which language is this document in
 * -----------------------------------------------------------------------------
 * Asked once, at the moment somebody presses Excel or PDF, because it is the
 * only moment they know the answer: the same person exports an English payslip
 * for the file and an Arabic one for the labour office in the same afternoon.
 *
 * -----------------------------------------------------------------------------
 * One language, not two
 * -----------------------------------------------------------------------------
 * English means a document with no Arabic in it at all, laid out left to right.
 * Arabic means the whole of it in Arabic, laid out right to left. Not a
 * bilingual hybrid with English labels and an Arabic heading — that reads as
 * neither, and the person who needs the Arabic one usually needs it because
 * somebody official asked for it.
 *
 * -----------------------------------------------------------------------------
 * Not asked when there is nothing to ask
 * -----------------------------------------------------------------------------
 * A company that has not switched on the second language gets English without
 * being asked. A dialog with one real answer is a tax on everybody who will
 * never use the other one.
 * ========================================================================== */

type Resolver = (lang: DocLang | null) => void;

export interface ExportLanguageValue {
  /** Resolves to the chosen language, or null if the export was abandoned. */
  askLanguage: () => Promise<DocLang | null>;
}

/* The context object lives in ./exportLanguageContext, so its identity survives this
   module being re-evaluated by a hot reload. See src/core/i18nContext.ts. */


export function ExportLanguageProvider({ children }: { children: ReactNode }) {
  const t = useT();
  const { company } = useSession();
  const [open, setOpen] = useState(false);
  const resolver = useRef<Resolver | null>(null);

  const bilingual = Boolean(company?.bilingualDocuments);

  const askLanguage = useCallback(() => {
    if (!bilingual) return Promise.resolve<DocLang | null>('en');

    setOpen(true);
    return new Promise<DocLang | null>((resolve) => {
      resolver.current = resolve;
    });
  }, [bilingual]);

  function answer(lang: DocLang | null) {
    setOpen(false);
    resolver.current?.(lang);
    resolver.current = null;
  }

  return (
    <ExportLanguageContext.Provider value={{ askLanguage }}>
      {children}

      {open && (
        <div
          className="fixed inset-0 z-[60] grid place-items-end sm:place-items-center bg-black/60 sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-label={t('xl.chooseLanguage')}
          /* Dismissing is abandoning the export, not choosing English. Somebody
             who taps outside has not decided anything, and producing a document
             on their behalf is the kind of helpfulness that ends up in an
             inbox in the wrong language. */
          onClick={() => answer(null)}
        >
          <div
            className="w-full sm:max-w-sm bg-surface rounded-t-card sm:rounded-card border border-line pt-safe-plus-4 sm:pt-5 px-5 pb-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2">
              <Languages size={17} className="text-muted" />
              <h2 className="text-[15.5px] font-bold tracking-tight">
                Which language?
              </h2>
            </div>

            <p className="text-[12.5px] text-muted mt-1.5 leading-snug">
              {t('xl.wholeDocument')}
            </p>

            <div className="grid grid-cols-2 gap-2 mt-4">
              <LanguageButton onClick={() => answer('en')} name="English" />
              <LanguageButton onClick={() => answer('ar')} name="العربية" rtl />
            </div>

            <div className="flex justify-end mt-3">
              <Button variant="ghost" size="sm" onClick={() => answer(null)}>{t('common.cancel')}</Button>
            </div>
          </div>
        </div>
      )}
    </ExportLanguageContext.Provider>
  );
}

function LanguageButton({
  onClick, name, rtl,
}: {
  onClick: () => void; name: string; rtl?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-card border border-line bg-surface hover:border-brand hover:bg-brand-subtle',
        'px-3 py-4 text-center transition-colors',
      )}
    >
      {/* The Arabic option is written in Arabic. A button reading "Arabic" in
          English is the one thing on this dialog somebody who reads only Arabic
          could not use. */}
      {/* The name of the language and nothing else. Which way a page runs is
          how the document gets built, not a choice being offered — somebody
          picking a language has already decided everything the caption was
          telling them. */}
      <span className="block text-[15px] font-bold" dir={rtl ? 'rtl' : 'ltr'}>{name}</span>
    </button>
  );
}

export function useExportLanguage(): ExportLanguageValue {
  const ctx = useContext(ExportLanguageContext);
  if (!ctx) {
    /* Rather than silently defaulting to English. A missing provider means every
       export in that tree quietly loses the choice, which is the kind of fault
       that ships. */
    throw new Error('useExportLanguage must be used inside <ExportLanguageProvider>');
  }
  return ctx;
}
